import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { t } from 'i18next';
import {
  UserCircleIcon,
  GlobeAltIcon,
  LinkIcon,
} from '@heroicons/react/24/outline';

import BrandHeroCarousel from 'components/BrandHeroCarousel';
import CategoryList from 'components/CategoryList';
import CategoryOverflow from 'components/CategoryOverflow';
import BrandCompactCover from './BrandCompactCover';
import facebookIcon from 'assets/icons/socials/facebook_grey.svg';
import instagramIcon from 'assets/icons/socials/instagram_grey.svg';
import twitterIcon from 'assets/icons/socials/twitter_grey.svg';
import tiktokIcon from 'assets/icons/socials/tiktok_grey.svg';

const DummyRibbon = ({ label = t('listing.example') }) => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none z-10 rounded-3xl">
    <div
      className="absolute bg-amber-500 text-white text-[11px] font-bold uppercase tracking-widest text-center leading-[26px] h-[26px]"
      style={{
        top: '32px',
        right: '-38px',
        width: '170px',
        transform: 'rotate(45deg)',
        boxShadow: '0 2px 6px rgba(0,0,0,.18)',
      }}
    >
      {label}
    </div>
  </div>
);

const normUrl = (u) => {
  if (!u || typeof u !== 'string') return null;
  const s = u.trim();
  if (!s) return null;
  return /^https?:\/\//i.test(s) ? s : `https://${s}`;
};

const SocialIconButton = ({ href, label, children }) =>
  href ? (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      title={label}
      onClick={(e) => e.stopPropagation()}
      className="inline-flex items-center justify-center w-7 h-7 rounded-full text-gray-500 hover:text-ink-strong hover:bg-paper transition"
    >
      {children}
    </a>
  ) : null;

// Two-up grid card for the brands listing on phones: cover, round logo
// overlapping it with the categories beside it, then name and location. No
// description, to keep the two columns short.
const BrandCompactCard = ({ brand, onOpen }) => {
  const { avatar, username, categories, city, country, media } = brand;
  const locationLabel =
    city && country ? `${city}, ${country}` : city || country || '';

  return (
    <div
      role={onOpen ? 'button' : undefined}
      tabIndex={onOpen ? 0 : undefined}
      onClick={onOpen}
      onKeyDown={
        onOpen
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') onOpen();
            }
          : undefined
      }
      className={`relative bg-white rounded-2xl ring-1 ring-line shadow-card-listing flex flex-col overflow-hidden h-full ${
        onOpen ? 'cursor-pointer' : ''
      }`}
    >
      {brand.isDummy && <DummyRibbon />}

      <div className="relative aspect-[16/9] bg-paper overflow-hidden">
        <BrandCompactCover media={media} name={username} />
      </div>

      <div className="-mt-7 px-2.5 flex items-end gap-1.5 relative z-10">
        <div className="w-14 h-14 rounded-full overflow-hidden ring-[3px] ring-white shadow-card bg-paper flex items-center justify-center shrink-0">
          {avatar ? (
            <img
              src={avatar}
              alt={username || 'Brand'}
              className="w-full h-full object-cover"
              loading="lazy"
              decoding="async"
            />
          ) : (
            <UserCircleIcon className="w-8 h-8 text-gray-300" />
          )}
        </div>
        <div className="flex-1 min-w-0 flex justify-end pb-0.5">
          <CategoryList
            categories={categories}
            onlyMacro
            withLabel
            tapToReveal
            macroIconClass="w-[18px] h-[18px]"
            containerClass="flex flex-row flex-nowrap items-center justify-end gap-[3px] overflow-hidden"
          />
        </div>
      </div>

      <div className="px-2.5 pt-1.5 pb-3 min-w-0">
        <h3 className="font-extrabold text-[14px] text-ink-strong leading-tight line-clamp-2 break-words tracking-tight">
          {username}
        </h3>
        {locationLabel && (
          <p className="mt-0.5 text-[11.5px] text-gray-500 truncate">
            {locationLabel}
          </p>
        )}
      </div>
    </div>
  );
};

const BrandCardWrapper = ({ brand, hasNavigation, compact = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  if (!brand) return null;

  const {
    avatar,
    username,
    categories,
    description,
    city,
    country,
    media,
    activeUpcomingEventsCount = 0,
    socials = {},
  } = brand;

  const locationLabel =
    city && country ? `${city}, ${country}` : city || country || '';

  const { website, facebook, instagram, twitter, tiktok, others = [] } =
    socials || {};
  const hasAnySocial = !!(
    normUrl(website) ||
    normUrl(facebook) ||
    normUrl(instagram) ||
    normUrl(twitter) ||
    normUrl(tiktok) ||
    (Array.isArray(others) && others.some((o) => normUrl(o?.url)))
  );

  const handleClick = () => {
    if (hasNavigation && brand?.slug) navigate(`/brands/${brand.slug}`);
  };

  if (compact) {
    return (
      <BrandCompactCard
        brand={brand}
        onOpen={hasNavigation ? handleClick : undefined}
      />
    );
  }

  const eventsLine =
    activeUpcomingEventsCount > 0 ? (
      <span className="text-[11px] text-gray-600 italic">
        {t('listing.confirmed')}
        <span className="font-bold text-ink-strong not-italic">
          {activeUpcomingEventsCount}
        </span>
        {activeUpcomingEventsCount === 1
          ? t('listing.event')
          : t('listing.events')}
      </span>
    ) : (
      <span className="text-[11px] text-gray-400 italic">
        {t('listing.no_confirmations')}
      </span>
    );

  return (
    <div
      role={hasNavigation ? 'button' : undefined}
      tabIndex={hasNavigation ? 0 : undefined}
      onClick={hasNavigation ? handleClick : undefined}
      onKeyDown={
        hasNavigation
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') handleClick();
            }
          : undefined
      }
      className={`relative bg-white rounded-3xl ring-1 ring-line shadow-card-listing hover:shadow-lg transition flex flex-col overflow-hidden h-full ${
        hasNavigation ? 'cursor-pointer' : ''
      }`}
    >
      {brand.isDummy && <DummyRibbon />}

      {/* Hero strip */}
      <BrandHeroCarousel images={media} className="h-[180px]" />

      {/* Avatar overlap + categories */}
      {/* Overlaps the bottom of the hero: let swipes/clicks fall through to it. */}
      <div className="-mt-9 px-4 flex items-end gap-3 relative z-10 pointer-events-none">
        <div className="w-[72px] h-[72px] rounded-full overflow-hidden ring-4 ring-white shadow-card bg-paper flex items-center justify-center shrink-0">
          {avatar ? (
            <img
              src={avatar}
              alt={username || 'Brand'}
              className="w-full h-full object-cover"
              loading="lazy"
              decoding="async"
            />
          ) : (
            <UserCircleIcon className="w-10 h-10 text-gray-300" />
          )}
        </div>
        <div className="flex-1 min-w-0 flex items-end justify-end">
          <div className="pointer-events-auto">
            <CategoryOverflow
              categories={categories}
              maxVisible={7}
              macroIconClass="w-6 h-6"
              align="end"
            />
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="px-4 pt-3 pb-2">
        <div className="flex items-center gap-2 min-w-0">
          <h3 className="font-extrabold text-[17px] text-ink-strong tracking-tight truncate">
            {username}
          </h3>
        </div>
        {locationLabel && (
          <p className="text-[12px] text-gray-500 truncate">{locationLabel}</p>
        )}
        <p className="mt-2 text-[12.5px] text-gray-600 italic leading-snug break-words min-h-[4.5em]">
          {(description || '').slice(0, 200) || t('listing.no_description')}
        </p>
      </div>

      {/* Footer */}
      <div className="mt-auto px-4 py-3 border-t border-line/70 flex items-center justify-between gap-2">
        {eventsLine}
        {hasAnySocial && (
          <div className="flex items-center gap-1">
            <SocialIconButton href={normUrl(website)} label="Website">
              <GlobeAltIcon className="w-3.5 h-3.5" />
            </SocialIconButton>
            <SocialIconButton href={normUrl(instagram)} label="Instagram">
              <img src={instagramIcon} alt="" className="w-3.5 h-3.5" />
            </SocialIconButton>
            <SocialIconButton href={normUrl(facebook)} label="Facebook">
              <img src={facebookIcon} alt="" className="w-3.5 h-3.5" />
            </SocialIconButton>
            <SocialIconButton href={normUrl(twitter)} label="Twitter / X">
              <img src={twitterIcon} alt="" className="w-3.5 h-3.5" />
            </SocialIconButton>
            <SocialIconButton href={normUrl(tiktok)} label="TikTok">
              <img src={tiktokIcon} alt="" className="w-3.5 h-3.5" />
            </SocialIconButton>
            {Array.isArray(others) &&
              others.map((o, i) => (
                <SocialIconButton
                  key={i}
                  href={normUrl(o?.url)}
                  label={o?.label || 'Link'}
                >
                  <LinkIcon className="w-3.5 h-3.5" />
                </SocialIconButton>
              ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BrandCardWrapper;
